import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ClientUsers component
 *
 * This component displays and manages client-specific users.
 * For the Guild client, use the `SystemUsers` component instead.
 *
 */
import { useMutation } from '@apollo/client';
import { Alert, Card, CardContent } from '@guildeducationinc/recess';
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import { AdminTopNav } from '@src/components/Admin/AdminTopNav/AdminTopNav';
import { AdminContext } from '@src/contexts/AdminContextProvider';
import { CREATE_USER, UPDATE_USER, USERS } from '@src/graphql/queries/user';
import { useAuth } from '@src/hooks/useAuth';
import { useUsers } from '@src/hooks/useUsers';
import { addMonths, format, isValid, parseISO } from 'date-fns';
import { pick } from 'lodash';
import { DataTable, Form, MfaMode, Permission, Role, SearchFilterBar, TrackableButton, trimObjectStringFields, useAlert, useLocale, } from 'pe-core-frontend';
import { useContext, useMemo, useState } from 'react';
export const ClientUsers = ({ client }) => {
    const { hasPermission } = useAuth();
    const { t, formatToLocalDatetime } = useLocale();
    const { showAlert } = useAlert();
    const { isUsingGuildAuth } = useContext(AdminContext);
    const refetchQueries = (result) => {
        if (!result.data)
            return [];
        if (result.data.createUser?.validationErrors ||
            result.data.createUser?.error ||
            result.data.updateUser?.validationErrors ||
            result.data.updateUser?.error) {
            return [];
        }
        return [USERS];
    };
    const [searchText, setSearchText] = useState('');
    const { users, loading, error, page, pageSize, setPage, setPageSize, total, orderBy, orderByDir, setOrderBy, setOrderByDir, } = useUsers({
        clientId: client.id,
        searchText,
    });
    const [createUser] = useMutation(CREATE_USER, {
        refetchQueries,
    });
    const [updateUser] = useMutation(UPDATE_USER, {
        refetchQueries,
    });
    const [selectedUser, setSelectedUser] = useState(null);
    const [saveError, setSaveError] = useState('');
    const handleSortModelChange = (sortModel) => {
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            setOrderBy(sort.field);
            setOrderByDir(sort.sort);
        }
        else {
            setOrderBy(null);
            setOrderByDir(null);
        }
    };
    const UserFields = useMemo(() => {
        const fields = [
            {
                name: 'firstName',
                label: t('users.fields.firstName'),
                type: 'text',
            },
            {
                name: 'lastName',
                label: t('users.fields.lastName'),
                type: 'text',
            },
            {
                name: 'email',
                label: t('users.fields.email'),
                type: 'text',
            },
            {
                name: 'uniqueId',
                label: t('users.fields.uniqueId'),
                type: 'text',
                show: isUsingGuildAuth,
                infoText: t("Employee ID is required when using Guild Auth. This must match the employee ID in Guild's employee profile"),
            },
            {
                name: 'isActive',
                label: t('users.fields.isActive'),
                type: 'boolean',
                serializeValue: (value) => (value ? 'true' : 'false'),
                deserializeValue: (value) => value === 'true',
            },
            {
                name: 'mfasEnabled',
                label: t('users.fields.mfasEnabled'),
                type: 'lookup',
                multiple: true,
                options: Object.keys(MfaMode).map(mode => ({
                    value: mode,
                    label: t(mode),
                })),
                show: !isUsingGuildAuth,
            },
            {
                name: 'roleNames',
                label: t('users.fields.roleNames'),
                type: 'lookup',
                multiple: true,
                options: [{ value: Role.MANAGER, label: t(Role.MANAGER) }],
            },
            {
                name: 'disableAt',
                label: t('users.fields.disableAt'),
                type: 'date',
            },
            {
                // When client is using Guild Auth, show password field when adding a new user.
                // - Password is required to create a user, even though it's never used for Guild Auth.
                // - Hide password when updating a Guild Auth user, since changing it would have no effect.
                // When client is not using Guild Auth, always show password field.
                name: 'password',
                label: t('users.fields.password'),
                type: 'password',
                hidden: isUsingGuildAuth && Boolean(selectedUser?.id),
                infoText: isUsingGuildAuth
                    ? t('This client is using Guild Auth. Password is still required, but the value will not be used for Guild Auth.')
                    : undefined,
            },
        ];
        // Only include fields flagged to show
        return fields.filter(f => f.show !== false);
    }, [t, isUsingGuildAuth]);
    const handleValidate = (user) => {
        const errors = {};
        user = trimObjectStringFields(user, ['firstName', 'lastName', 'email', 'uniqueId']);
        if (!user.firstName) {
            errors.firstName = t('First Name is required');
        }
        if (!user.lastName) {
            errors.lastName = t('Last Name is required');
        }
        if (!user.email) {
            errors.email = t('Email is required');
        }
        if (!user.uniqueId && isUsingGuildAuth) {
            errors.uniqueId = t('Employee ID is required when using Guild Auth');
        }
        if (!user.roleNames || user.roleNames.length === 0) {
            errors.roleNames = t('Role is required');
        }
        if (!user.id) {
            if (!user.password) {
                if (isUsingGuildAuth) {
                    errors.password = t('Password must be provided, but is not used when using Guild Auth');
                }
                else {
                    errors.password = t('Password is required');
                }
            }
        }
        return errors;
    };
    const handleSave = async (user) => {
        let result;
        try {
            const mutation = user.id ? updateUser : createUser;
            const prop = user.id ? 'updateUser' : 'createUser';
            const errors = handleValidate(user);
            if (Object.keys(errors).length > 0) {
                setSaveError(Object.values(errors).join(', '));
                return;
            }
            setSaveError('');
            result = await mutation({
                variables: {
                    input: {
                        ...pick(user, [
                            'clientId',
                            'firstName',
                            'lastName',
                            'email',
                            'uniqueId',
                            'mfasEnabled',
                            'roleNames',
                            'isActive',
                            'disableAt',
                        ]),
                        id: user.id ? user.id : undefined,
                        password: user.password ? user.password : undefined,
                    },
                },
            });
            if (result.data) {
                if (result.data[prop]?.validationErrors) {
                    return result.data[prop]?.validationErrors;
                }
                if (result.data[prop]?.error) {
                    const errorMsg = result.data[prop].error;
                    if (errorMsg.startsWith('Unique ID {employeeId} is already used by another')) {
                        setSaveError(t(errorMsg, {
                            values: { employeeId: user.uniqueId },
                        }));
                    }
                    else {
                        setSaveError(t(errorMsg));
                    }
                    return;
                }
            }
            showAlert(t('User saved'));
            setSelectedUser(null);
        }
        catch (e) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const msg = e.message;
            setSaveError(msg);
        }
    };
    const getRoles = (user) => {
        const isSuperuser = user.isSuperuser;
        const roles = (isSuperuser ? [Role.SYS_ADMIN.toString()] : []).concat((user.roleNames || []));
        return roles;
    };
    const handleAddUser = () => {
        setSelectedUser({
            id: 0,
            clientId: client?.id || 0,
            firstName: '',
            lastName: '',
            email: '',
            uniqueId: '',
            isActive: true,
            isVerified: false,
            isSuperuser: false,
            mfasEnabled: [],
            disableAt: addMonths(new Date(), 6).toISOString().slice(0, 10), // Default disable date to 6 months from now
            roleNames: [Role.MANAGER], // Default role to manager for non-Guild users
        });
    };
    return (_jsxs("div", { children: [_jsx(AdminTopNav, { title: t('Users'), searchFilterActions: _jsx(SearchFilterBar, { enableSearch: true, searchText: searchText, onSearchChange: text => {
                        setSearchText(text);
                        setPage(1);
                    }, searchPlaceholder: t('Search by Name/Email') }), actions: hasPermission(Permission.CREATE_USER) && (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'mui', enText: 'Add User', tag: 'header', color: 'primary', variant: 'contained', type: 'button', className: 'button button--primary margin--bottom_16', onClick: handleAddUser, children: t('users.add') }) })) }), _jsx("div", { children: _jsx(Card, { children: _jsxs(CardContent, { children: [error && (_jsx(Alert, { display: true, hideClose: true, type: 'danger', children: error.message })), _jsxs(_Fragment, { children: [_jsx(DataTable, { disableColumnMenu: true, disableRowSelectionOnClick: true, rows: users, pageSizeOptions: [10, 25], paginationMode: 'server', paginationModel: { page: page - 1, pageSize }, onPaginationModelChange: pagination => {
                                            setPage(pagination.page + 1);
                                            setPageSize(pagination.pageSize);
                                        }, onSortModelChange: handleSortModelChange, sortModel: [{ field: orderBy ?? 'firstName', sort: orderByDir }], rowCount: total, loading: loading, columns: [
                                            {
                                                field: 'firstName',
                                                headerName: t('First Name'),
                                                flex: 0.2,
                                            },
                                            {
                                                field: 'lastName',
                                                headerName: t('Last Name'),
                                                flex: 0.2,
                                            },
                                            {
                                                field: 'email',
                                                headerName: t('Email'),
                                                flex: 0.2,
                                            },
                                            ...(isUsingGuildAuth
                                                ? [
                                                    {
                                                        field: 'uniqueId',
                                                        headerName: t('users.fields.uniqueId'),
                                                        flex: 0.2,
                                                    },
                                                ]
                                                : []),
                                            {
                                                field: 'isActive',
                                                headerName: t('Active?'),
                                                valueGetter: e => (e.row.isActive ? 'Yes' : 'No'),
                                                flex: 0.2,
                                                sortable: true,
                                            },
                                            {
                                                field: 'roleNames',
                                                headerName: t('Roles'),
                                                flex: 0.2,
                                                sortable: false,
                                                renderCell: ({ row }) => {
                                                    return getRoles(row)
                                                        .map(r => t(r))
                                                        .join(', ');
                                                },
                                            },
                                            {
                                                field: 'disableAt',
                                                headerName: t('Disable At'),
                                                flex: 0.2,
                                                sortable: true,
                                                renderCell: ({ row }) => {
                                                    if (!row.disableAt)
                                                        return '';
                                                    const date = parseISO(row.disableAt);
                                                    return isValid(date) ? format(date, 'MM-dd-yyyy') : row.disableAt;
                                                },
                                            },
                                            ...(isUsingGuildAuth
                                                ? [
                                                    {
                                                        field: 'productAccessSyncStatus',
                                                        headerName: t('Product Access Sync Status'),
                                                        flex: 0.2,
                                                        sortable: true,
                                                    },
                                                    {
                                                        field: 'productAccessSyncedAt',
                                                        headerName: t('Product Access Synced At'),
                                                        flex: 0.2,
                                                        sortable: true,
                                                        renderCell: ({ row }) => (_jsx("span", { children: row.productAccessSyncedAt
                                                                ? formatToLocalDatetime(row.productAccessSyncedAt, {
                                                                    dateStyle: 'short',
                                                                    timeStyle: 'medium',
                                                                })
                                                                : '' })),
                                                    },
                                                ]
                                                : []),
                                            {
                                                field: 'action',
                                                headerName: t('Actions'),
                                                renderCell: ({ row }) => (_jsx("div", { children: _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `user_${row.id}`, label: t('Edit'), appearance: 'minimal', icon: _jsx(EditIcon, { size: 20 }), onClick: () => setSelectedUser(row), className: 'margin--right_8' }) })),
                                                width: 100,
                                                sortable: false,
                                            },
                                        ] }), selectedUser && (_jsx(Form, { title: selectedUser.id ? 'Edit User' : 'Add User', modal: true, obj: { ...selectedUser, roleNames: getRoles(selectedUser) }, fields: UserFields, onValidate: handleValidate, onSave: handleSave, onCancel: () => {
                                            setSelectedUser(null);
                                            setSaveError('');
                                        }, error: saveError }))] })] }) }) })] }));
};
